import styles from "../features/teams/components/TeamSettings.module.css";
import { useCallback, useEffect, useRef, useState } from "react";
import useCauseInput from "./useCauseInput";
import i18n from "i18next";
import { addCause } from "@/api/causeApi";
import { useCause } from "../contexts/CauseContext";

type CauseInputProps = {
  isAddYourOwnCause: boolean;
  setIsAddYourOwnCause: (isAddYourOwnCause: boolean) => void;
  yourOwnCause: string;
  setYourOwnCause: (yourOwnCause: string) => void;
  setYourOwnCauseTypeId: (causeTypeId: string) => void;
};

export const CauseInput = ({
  isAddYourOwnCause,
  setIsAddYourOwnCause,
  yourOwnCause,
  setYourOwnCause,
  setYourOwnCauseTypeId,
}: CauseInputProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollFieldAboveKeyboard = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!isAddYourOwnCause) return;
    const t = window.setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      scrollFieldAboveKeyboard();
    }, 50);
    return () => window.clearTimeout(t);
  }, [isAddYourOwnCause, scrollFieldAboveKeyboard]);

  useEffect(() => {
    if (!isAddYourOwnCause) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const onViewportChange = () => scrollFieldAboveKeyboard();
    vv.addEventListener("resize", onViewportChange);
    vv.addEventListener("scroll", onViewportChange);
    return () => {
      vv.removeEventListener("resize", onViewportChange);
      vv.removeEventListener("scroll", onViewportChange);
    };
  }, [isAddYourOwnCause, scrollFieldAboveKeyboard]);

  const { validateCause, userId } = useCauseInput();
  const { setCauseTypes, setActiveCauseTypes } = useCause() ?? {};

  async function saveCause(cause: string) {
    console.log("saveCause  " + cause);
    validateCause(cause).then((r) => {
      console.log("validated cause");
      console.log(r);

      if (!r) {
        setError("Failed to validate cause");
      }
    });
    setIsSaving(true);
    const normalizedCause = cause.trim();

    try {
      const createdId = await addCause({
        causeTypeId: null,
        userId,
        feelingId: null,
        name: normalizedCause,
        teamId: null
      });

      if (createdId != null) {
        const createdCauseId = String(createdId);
        const newCause = {
          id: createdCauseId,
          mood: "Flow",
          uid: userId,
          direction: "",
          archived: false,
          causeTypeTranslation: { [i18n.language]: normalizedCause },
        };

        if (setCauseTypes && typeof setCauseTypes === "function") {
          setCauseTypes((prev: unknown[]) => [...(prev || []), newCause]);
        }
        if (setActiveCauseTypes && typeof setActiveCauseTypes === "function") {
          setActiveCauseTypes((prev: unknown[]) => [...(prev || []), newCause]);
        }

        setYourOwnCauseTypeId(createdCauseId);
      }

      console.log("finish saveCause " + JSON.stringify(createdId));
    } catch (e) {
      console.error("Error saving cause:", e);
      setError("Failed to save cause");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      {!isAddYourOwnCause && false && (
        <button
          className="strength-button select-component__duration-button"
          onClick={() => {
            setIsAddYourOwnCause(true);
            console.log("show input");
          }}
        >
          + Add your own cause!
        </button>
      )}
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {isAddYourOwnCause && (
            <input
              ref={inputRef}
              id="add-team-name"
              className={styles["team-settings-invite-form-input"]}
              type="text"
              enterKeyHint="done"
              onFocus={scrollFieldAboveKeyboard}
              onChange={(e) => {
                setYourOwnCause(e.target.value);
                setYourOwnCauseTypeId("");
                setError(""); // clear error on typing
                setIsSaving(false);
              }}
              style={{ color: "black", paddingLeft: "18px" }}
              value={yourOwnCause}
              placeholder="Enter your own cause..."
              autoComplete="off"
            />
          )}

          {yourOwnCause.length > 0 && !isSaving && (
            <button
              style={{ marginRight: "18px" }}
              onClick={() => {
                if (!yourOwnCause.trim()) {
                  setError("Cause cannot be empty");
                  return;
                }

                if (yourOwnCause.length < 3) {
                  setError("Cause must be at least 3 characters");
                  return;
                }

                saveCause(yourOwnCause);
              }}
            >
              Add
            </button>
          )}
        </div>

        {error && (
          <span style={{ color: "red", marginLeft: "18px", marginTop: "6px" }}>
            {error}
          </span>
        )}
      </div>
    </>
  );
};
